import { useEffect, useState } from 'react';
import {
    UserCircle,
    Barbell,
    Trophy,
    ChartBar,
    SignOut,
} from '@phosphor-icons/react';
import { useAuth, formatApiErrorDetail } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { WorkoutCuesForm } from '@/components/WorkoutCuesForm';

const EXPERIENCE_OPTIONS = [
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' },
];
const GOAL_OPTIONS = [
    { value: 'strength', label: 'Strength' },
    { value: 'hypertrophy', label: 'Hypertrophy' },
    { value: 'endurance', label: 'Endurance' },
    { value: 'general', label: 'General' },
];

export default function Account() {
    const { user, updateProfile, logout } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        name: '',
        weight: '',
        height: '',
        units: 'kg',
        experience_level: '',
        goal: '',
    });
    const [stats, setStats] = useState(null);
    const [savedAt, setSavedAt] = useState(null);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!user) return;
        setForm({
            name: user.name || '',
            weight: user.weight ?? '',
            height: user.height ?? '',
            units: user.units || 'kg',
            experience_level: user.experience_level || '',
            goal: user.goal || '',
        });
    }, [user]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get('/sessions/stats/summary');
                if (!cancelled) setStats(data);
            } catch {
                /* ignore */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    async function onSave(e) {
        e.preventDefault();
        setError('');
        setSaving(true);
        const patch = {
            name: form.name || undefined,
            weight: form.weight === '' ? undefined : Number(form.weight),
            height: form.height === '' ? undefined : Number(form.height),
            units: form.units,
            experience_level: form.experience_level || undefined,
            goal: form.goal || undefined,
        };
        try {
            await updateProfile(patch);
            setSavedAt(Date.now());
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    }

    async function onLogout() {
        await logout();
        navigate('/', { replace: true });
    }

    async function onSaveCues(payload) {
        setError('');
        try {
            await updateProfile({ workout_cue_prefs: payload });
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
            throw err;
        }
    }

    if (!user) return null;

    return (
        <main
            data-testid="account-root"
            className="mx-auto max-w-4xl px-5 sm:px-8 pt-10 sm:pt-14 pb-32"
        >
            <div className="animate-fade-up">
                <span className="uppercase-mono text-muted-foreground">
                    Account · {user.role}
                </span>
                <h1 className="mt-2 font-display font-extrabold tracking-tighter text-4xl sm:text-5xl">
                    Welcome,{' '}
                    <span className="text-muted-foreground">{user.name}.</span>
                </h1>
                <p className="mt-3 text-muted-foreground">
                    Signed in via{' '}
                    {(user.auth_methods || []).join(' + ') || 'password'} ·{' '}
                    {user.email}
                </p>
            </div>

            {/* Stats */}
            <section className="mt-10 grid sm:grid-cols-4 gap-4">
                <StatTile
                    icon={<Barbell size={18} weight="bold" />}
                    label="Sessions"
                    value={stats?.total_sessions ?? '—'}
                />
                <StatTile
                    icon={<ChartBar size={18} weight="bold" />}
                    label="Sets logged"
                    value={stats?.total_sets ?? '—'}
                />
                <StatTile
                    icon={<ChartBar size={18} weight="bold" />}
                    label="Volume"
                    value={
                        stats
                            ? `${Math.round(stats.total_volume_kg).toLocaleString()} ${user.units}`
                            : '—'
                    }
                />
                <StatTile
                    icon={<Trophy size={18} weight="fill" />}
                    label="PRs"
                    value={stats?.prs ?? '—'}
                    accent="#FF4D4D"
                />
            </section>

            {/* Profile form */}
            <section className="mt-10 glass-card rounded-3xl p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                    <span className="grid place-items-center w-10 h-10 rounded-2xl bg-foreground/5 border border-foreground/10">
                        <UserCircle size={20} weight="bold" />
                    </span>
                    <div>
                        <p className="uppercase-mono text-muted-foreground">
                            Profile
                        </p>
                        <h2 className="font-display font-bold text-xl tracking-tight">
                            Body metrics & training profile
                        </h2>
                    </div>
                </div>

                <form onSubmit={onSave} className="grid sm:grid-cols-2 gap-4">
                    <TextField
                        label="Name"
                        testId="account-name-input"
                        value={form.name}
                        onChange={(v) => setForm((f) => ({ ...f, name: v }))}
                    />
                    <SelectField
                        label="Units"
                        testId="account-units-select"
                        value={form.units}
                        onChange={(v) => setForm((f) => ({ ...f, units: v }))}
                        options={[
                            { value: 'kg', label: 'Kilograms (kg)' },
                            { value: 'lb', label: 'Pounds (lb)' },
                        ]}
                    />
                    <NumberField
                        label={`Body weight (${form.units})`}
                        testId="account-weight-input"
                        value={form.weight}
                        onChange={(v) => setForm((f) => ({ ...f, weight: v }))}
                    />
                    <NumberField
                        label="Height (cm)"
                        testId="account-height-input"
                        value={form.height}
                        onChange={(v) => setForm((f) => ({ ...f, height: v }))}
                    />
                    <SelectField
                        label="Experience"
                        testId="account-experience-select"
                        value={form.experience_level}
                        onChange={(v) =>
                            setForm((f) => ({ ...f, experience_level: v }))
                        }
                        options={EXPERIENCE_OPTIONS}
                        placeholder="Select level"
                    />
                    <SelectField
                        label="Goal"
                        testId="account-goal-select"
                        value={form.goal}
                        onChange={(v) => setForm((f) => ({ ...f, goal: v }))}
                        options={GOAL_OPTIONS}
                        placeholder="Select goal"
                    />

                    <div className="sm:col-span-2">
                        {error && (
                            <p
                                data-testid="account-error"
                                className="text-sm text-[#FF4D4D] font-medium mb-3"
                            >
                                {error}
                            </p>
                        )}
                        <div className="flex flex-wrap items-center gap-3">
                            <button
                                type="submit"
                                data-testid="account-save-button"
                                disabled={saving}
                                className="h-12 px-6 rounded-2xl bg-primary text-primary-foreground font-bold tracking-tight shadow-neon hover:-translate-y-[2px] active:scale-[0.99] transition-all disabled:opacity-60"
                            >
                                {saving ? 'Saving…' : 'Save profile'}
                            </button>
                            {savedAt && (
                                <span className="uppercase-mono text-[#00FF9D]">
                                    Saved · just now
                                </span>
                            )}
                        </div>
                    </div>
                </form>
            </section>

            {/* Workout cues */}
            <WorkoutCuesForm
                initial={user.workout_cue_prefs}
                onSave={onSaveCues}
                saving={saving}
            />

            {/* Danger zone */}
            <section className="mt-8 flex items-center justify-between rounded-2xl border border-foreground/10 bg-foreground/[0.02] px-5 py-4">
                <div>
                    <p className="font-semibold tracking-tight">Sign out</p>
                    <p className="text-sm text-muted-foreground">
                        End this session on this device.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={onLogout}
                    data-testid="logout-button"
                    className="h-11 px-5 rounded-2xl border border-foreground/15 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.98] transition-all inline-flex items-center gap-2 font-semibold"
                >
                    <SignOut size={16} weight="bold" />
                    Log out
                </button>
            </section>
        </main>
    );
}

function StatTile({ icon, label, value, accent }) {
    return (
        <div className="glass-card rounded-2xl p-5">
            <div className="flex items-center justify-between mb-2">
                <span className="uppercase-mono text-muted-foreground">
                    {label}
                </span>
                <span
                    className="grid place-items-center w-7 h-7 rounded-full"
                    style={{
                        background: accent ? `${accent}22` : 'rgba(127,127,127,0.12)',
                        color: accent || 'currentColor',
                    }}
                >
                    {icon}
                </span>
            </div>
            <p
                className="font-mono text-2xl font-bold tabular-nums tracking-tight"
                style={{ color: accent || undefined }}
            >
                {value}
            </p>
        </div>
    );
}

function TextField({ label, value, onChange, testId }) {
    return (
        <label className="block">
            <span className="uppercase-mono text-muted-foreground block mb-2">
                {label}
            </span>
            <input
                type="text"
                data-testid={testId}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full h-12 rounded-2xl border border-foreground/15 bg-foreground/5 px-4 outline-none focus:border-foreground/40 transition-colors"
            />
        </label>
    );
}

function NumberField({ label, value, onChange, testId }) {
    return (
        <label className="block">
            <span className="uppercase-mono text-muted-foreground block mb-2">
                {label}
            </span>
            <input
                type="number"
                step="0.1"
                inputMode="decimal"
                data-testid={testId}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full h-12 rounded-2xl border border-foreground/15 bg-foreground/5 px-4 outline-none focus:border-foreground/40 transition-colors font-mono tabular-nums"
            />
        </label>
    );
}

function SelectField({ label, value, onChange, testId, options, placeholder }) {
    return (
        <label className="block">
            <span className="uppercase-mono text-muted-foreground block mb-2">
                {label}
            </span>
            <select
                data-testid={testId}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full h-12 rounded-2xl border border-foreground/15 bg-foreground/5 px-4 outline-none focus:border-foreground/40 transition-colors text-foreground"
            >
                {placeholder && <option value="">{placeholder}</option>}
                {options.map((o) => (
                    <option key={o.value} value={o.value}>
                        {o.label}
                    </option>
                ))}
            </select>
        </label>
    );
}
